import { Helmet } from "react-helmet-async";

const SITE_URL = "https://journalmedica.com";
const DEFAULT_OG = `${SITE_URL}/og-image.jpg`;

/**
 * Per-page SEO component. Injects <title>, <meta description>, canonical,
 * OpenGraph, Twitter card, and optional BreadcrumbList JSON-LD.
 */
export default function SEO({
  title,
  description,
  path = "/",
  image = DEFAULT_OG,
  type = "website",
  noindex = false,
  jsonLd, // additional JSON-LD blocks (array)
  breadcrumbs, // [{name, url}]
}) {
  const url = `${SITE_URL}${path}`;
  const fullTitle = title
    ? `${title} · International Sci Ink Press`
    : "International Sci Ink Press — Advancing Medical Knowledge Through Open Science";

  const breadcrumbLd = breadcrumbs
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: b.url.startsWith("http") ? b.url : `${SITE_URL}${b.url}`,
        })),
      }
    : null;

  return (
    <Helmet>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="International Sci Ink Press Ltd" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* image:width/height + image:alt for richer link previews */}
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="International Sci Ink Press — Publishing Medical Research That Matters" />

      {breadcrumbLd && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbLd)}
        </script>
      )}
      {(jsonLd || []).map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
